import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { getConnection, getCurrentUser, hideSpinner, showSpinner } from 'src/app/core/services/functions.service';
import { HelpersService } from 'src/app/core/services/helpers.service';
import { LogisticaService } from '../services/logistica.service';
import { guardarCacheCausales, LiberarComponent, leerCacheCausales } from '../services/liberarCita.service';
import { LiberacionCasoService } from '../services/liberacion-caso.service';
import { CasoData, Causal, CausalUso, Consecuencia, ModoBusqueda, RegistroOp, SolicitudLiberacion } from './models/liberacion.models';
import { consecuenciasCaso, consecuenciasOp, etiquetaEstadoOp, filasPedidos, filasProcesos } from './liberacion.utils';
import { FilaRevision } from './components/formulario-liberacion/formulario-liberacion.component';

/**
 * Liberacion del pedido SAP de una OP o de un caso postventa.
 * ViewEncapsulation.None para que los estilos cubran tambien a los componentes hijos.
 */
@Component({
  selector: 'app-liberar-cita',
  templateUrl: './liberar-cita.component.html',
  styleUrls: ['./liberar-cita.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LiberarCitaComponent implements OnInit {

  @ViewChild('campoNumero') campoNumero?: ElementRef<HTMLInputElement>;

  stage = '';
  usuario_aplicativo = '';
  c_emp = '';
  motivos: Causal[] = [];
  /** Causales mas usadas en la ventana `diasTop` (vacio si la consulta falla: el formulario usa su respaldo). */
  topCausales: CausalUso[] = [];
  readonly diasTop = 30;
  /** true mientras no hay catalogo (ni guardado ni fresco) para pintar las causales. */
  cargandoCausales = true;
  errorCausales = false;

  modo: ModoBusqueda = 'OP';
  numero = '';
  errorBusqueda: string | null = null;
  /** Mensaje del back junto al buscador (OP entregada, no encontrada...). */
  aviso: string | null = null;
  /** Si el numero parece del otro modo, se ofrece cambiar. */
  sugerirModo: ModoBusqueda | null = null;

  cargando = false;
  enviando = false;
  exito: { mensaje: string; detalles: string[]; advertencia: string | null } | null = null;

  // OP
  registros: RegistroOp[] = [];
  entregasParciales = false;
  seleccionado: RegistroOp | null = null;
  mostrarErrorSeleccion = false;
  /** Numero de OP consultado (el campo puede cambiar despues). */
  opConsultada = '';

  // Caso
  caso: CasoData | null = null;

  constructor(
    private logisticaService: LogisticaService,
    private casoService: LiberacionCasoService,
    private helpers: HelpersService,
    private liberar: LiberarComponent
  ) { }

  ngOnInit(): void {
    // La exclamacion final es para que TypeScript confie en que el valor es string.
    this.usuario_aplicativo = getCurrentUser().usuario_aplicativo!;
    this.c_emp = getCurrentUser().c_emp!;
    this.cargarCausales(getConnection());
  }

  /**
   * Catalogo y causales mas usadas: se pinta de inmediato lo guardado (24 h) y se refresca por detras.
   * Sin nada guardado, el formulario muestra "Cargando causales..." hasta que llegue el catalogo.
   */
  private cargarCausales(company: string): void {
    const claveMotivos = 'motivos:' + company;
    const claveTop = 'top:' + this.c_emp + ':' + this.diasTop;
    const motivosGuardados = leerCacheCausales<Causal[]>(claveMotivos);
    const topGuardado = leerCacheCausales<CausalUso[]>(claveTop);

    if (motivosGuardados) { this.motivos = motivosGuardados; }
    if (topGuardado) { this.topCausales = topGuardado; }
    this.cargandoCausales = !motivosGuardados;

    this.liberar.get_motivo_rechazo(company).subscribe((result: any) => {
      const lista: Causal[] = (result && result['data']) || [];
      if (lista.length) {
        guardarCacheCausales(claveMotivos, lista);
        if (JSON.stringify(lista) !== JSON.stringify(this.motivos)) { this.motivos = lista; }
      }
      this.cargandoCausales = false;
      this.errorCausales = !this.motivos.length;
    }, () => {
      this.cargandoCausales = false;
      this.errorCausales = !this.motivos.length;
    });

    this.liberar.get_top_causales(this.c_emp, this.diasTop).subscribe(top => {
      if (top.length) {
        guardarCacheCausales(claveTop, top);
        if (JSON.stringify(top) !== JSON.stringify(this.topCausales)) { this.topCausales = top; }
      }
    });
  }

  /** Pais para el texto del atajo; c_emp es el codigo de empresa (JA Colombia, JP Panama). */
  get pais(): string {
    return this.c_emp === 'JP' ? 'Panamá' : 'Colombia';
  }

  // ---------- Lo que muestra la vista ----------

  get registroVisible(): RegistroOp | null {
    return this.seleccionado || (this.registros.length ? this.registros[0] : null);
  }

  etiquetaOp(registro: RegistroOp) {
    return etiquetaEstadoOp(registro.estado);
  }

  get filasPedidos() {
    return this.caso ? filasPedidos(this.caso) : [];
  }

  get filasProcesos() {
    return this.caso ? filasProcesos(this.caso) : [];
  }

  get motivoBloqueo(): string {
    const lado = this.caso && this.caso.lados.find(l => l.estado === 'bloqueado');
    return lado && lado.motivo ? lado.motivo : '';
  }

  get consecuencias(): Consecuencia[] | null {
    if (this.modo === 'CASO') return this.caso ? consecuenciasCaso(this.caso) : null;
    return consecuenciasOp(this.seleccionado, this.registros.length);
  }

  get filasRevision(): FilaRevision[] {
    if (this.modo === 'CASO' && this.caso) {
      return [
        { k: 'Caso', v: `${this.caso.num_caso} · ${this.caso.tipo_descripcion}` },
        { k: 'Cliente', v: this.caso.nombres }
      ];
    }
    const r = this.seleccionado;
    if (!r) return [];
    const rol = this.rolEntrega(this.registros.indexOf(r));
    return [
      { k: 'OP', v: `${this.opConsultada} · ${r.nombres}` },
      { k: 'Pedido SAP', v: r.num_pedido_sap ? `${rol} · ${r.num_pedido_sap}` : rol }
    ];
  }

  rolEntrega(i: number): string {
    return this.entregasParciales ? 'Entrega ' + (i + 1) : 'Pedido';
  }

  // ---------- Busqueda ----------

  cambiarModo(modo: ModoBusqueda, conservarNumero = false): void {
    this.modo = modo;
    if (!conservarNumero) this.numero = '';
    this.reiniciar();
    this.enfocarCampo();
  }

  /** Solo permite digitos al escribir o pegar. */
  alEscribir(valor: string): void {
    this.numero = valor.replace(/\D/g, '');
    this.errorBusqueda = null;
  }

  async buscar(): Promise<void> {
    if (!this.numero) {
      this.errorBusqueda = 'Escribe el número a buscar.';
      this.enfocarCampo();
      return;
    }

    const numero = this.numero;
    this.reiniciar();
    this.cargando = true;
    try {
      if (this.modo === 'CASO') {
        await this.buscarCaso(numero);
      } else {
        await this.buscarOp(numero);
      }
    } finally {
      this.cargando = false;
    }
  }

  private async buscarOp(numero: string): Promise<void> {
    const resp = await this.logisticaService.getDataRem(numero, this.c_emp, this.stage);

    if (resp.success === true) {
      this.opConsultada = numero;
      this.entregasParciales = resp.entregas_parciales === true;
      this.registros = resp.registros || [];
      // Una OP normal tiene un solo registro: se selecciona sola para no agregar un paso que antes no existia.
      if (!this.entregasParciales && this.registros.length > 0) this.seleccionado = this.registros[0];
    } else if (resp.codigo) {
      // Respuesta de negocio del back (ya entregada, no encontrada): se muestra tal cual junto al buscador.
      this.aviso = resp.message;
      // Los numeros de caso de Salesforce empiezan por cero.
      if (resp.codigo === 'OP_NO_ENCONTRADA' && numero.startsWith('0')) this.sugerirModo = 'CASO';
    } else {
      // Error tecnico (timeout, 502, red): se conserva el modal de hoy.
      this.helpers.notifications.errorDialog('Respuesta solicitud', resp.message);
    }
  }

  private async buscarCaso(numero: string): Promise<void> {
    const resp = await this.casoService.getDataCaso(numero, this.c_emp);

    if (resp.success && resp.data) {
      this.caso = resp.data;
    } else if (resp.codigo) {
      this.aviso = resp.message;
      if (!numero.startsWith('0')) this.sugerirModo = 'OP';
    } else {
      this.helpers.notifications.errorDialog('Respuesta solicitud', resp.message);
    }
  }

  irAlOtroModo(): void {
    if (!this.sugerirModo) return;
    this.cambiarModo(this.sugerirModo, true);
    this.buscar();
  }

  // ---------- Seleccion y liberacion ----------

  seleccionarRegistro(registro: RegistroOp): void {
    this.seleccionado = registro;
    this.mostrarErrorSeleccion = false;
  }

  alFaltarSeleccion(): void {
    this.mostrarErrorSeleccion = true;
    const lista = document.getElementById('picks-entrega');
    if (lista) {
      const primero = lista.querySelector('input') as HTMLInputElement | null;
      if (primero) primero.focus({ preventScroll: true });
      lista.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  async confirmar(solicitud: SolicitudLiberacion): Promise<void> {
    if (this.enviando) return;
    this.enviando = true;
    try {
      if (this.modo === 'CASO') {
        await this.liberarCaso(solicitud);
      } else {
        await this.liberarOp(solicitud);
      }
    } finally {
      this.enviando = false;
    }
  }

  private async liberarOp(solicitud: SolicitudLiberacion): Promise<void> {
    const registro = this.seleccionado;
    if (!registro) return this.alFaltarSeleccion();

    showSpinner('Liberando pedido, por favor espere...');
    const resp = await this.logisticaService.releaseOrder(
      this.opConsultada, this.c_emp, solicitud.detalle, this.usuario_aplicativo, this.stage,
      solicitud.causal.COD_CAUSAL, solicitud.causal.DES,
      registro, this.entregasParciales ? registro.id : null
    );
    hideSpinner();

    if (resp.success !== true) {
      this.helpers.notifications.errorDialog('Respuesta de solicitud', this.conDetalles(resp.message, resp.details));
      return;
    }

    // SEUS solo se toca si SAP se libero bien, y con la entrega elegida.
    showSpinner('Confirmando la cita, por favor espere...');
    const seus = await this.logisticaService.liberar_cita_seus(this.opConsultada, this.c_emp, registro);
    hideSpinner();

    this.exito = {
      mensaje: 'El pedido SAP se liberó y la OP vuelve a «Confirmación de cita».',
      detalles: resp.details || [],
      advertencia: seus.success ? null : 'El pedido se liberó, pero no se pudo devolver la OP a «Confirmación de cita». Avisa al equipo para revisarlo. ' + seus.message
    };
  }

  private async liberarCaso(solicitud: SolicitudLiberacion): Promise<void> {
    if (!this.caso) return;

    showSpinner('Liberando caso, por favor espere...');
    const resp = await this.casoService.releaseCase(this.caso, this.c_emp, this.usuario_aplicativo, solicitud);
    hideSpinner();

    if (resp.success !== true) {
      // El back deshace todo si un lado falla (igual que logistica-cambioparcial): no queda nada liberado a medias.
      this.helpers.notifications.errorDialog('Respuesta de solicitud', this.conDetalles(resp.message));
      return;
    }

    const rol = { R: 'Recogida', E: 'Entrega' };
    this.exito = {
      mensaje: 'SAP liberó los pedidos del caso.',
      detalles: (resp.resultados || []).map(r => `${rol[r.lado]}: ${r.resultado === 'liberado' ? 'pedido liberado.' : 'omitido. ' + r.mensaje}`),
      advertencia: null
    };
  }

  private conDetalles(mensaje: string, detalles: string[] = []): string {
    return (mensaje || '') + '<br/><br/><b>Detalles de liberacion :</b>' +
      (detalles.length ? detalles.map(d => '<br/>*' + d).join('') : '<br/>Ninguno a mostrar.');
  }

  gestionarOtro(): void {
    this.numero = '';
    this.reiniciar();
    this.enfocarCampo();
  }

  // ---------- Utilidades ----------

  /** Limpia el resultado, los avisos y el exito anteriores. */
  private reiniciar(): void {
    this.registros = [];
    this.entregasParciales = false;
    this.seleccionado = null;
    this.mostrarErrorSeleccion = false;
    this.caso = null;
    this.opConsultada = '';
    this.aviso = null;
    this.sugerirModo = null;
    this.errorBusqueda = null;
    this.exito = null;
  }

  private enfocarCampo(): void {
    setTimeout(() => this.campoNumero && this.campoNumero.nativeElement.focus());
  }
}
